"use client";

import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { ICasinoBet } from "@finbackoffice/clientbff-client";
import {
    ChoicesFilterExpression,
    ComparisonFilterExpression,
    FilterKind,
    IFilter,
    Order,
    StringFilterExpression,
} from "@finbackoffice/http-client";
import {
    CasinoScopesMap,
    ClientBFFContext,
    DEFAULT_PAGINATION,
    useDidUpdateEffect,
} from "@finbackoffice/site-core";
import { CasinoGameTypeEnum } from "@finbackoffice/fe-core";
import { useParams, useSearchParams } from "next/navigation";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import Loading from "components/base/loading/Loading";
import { useDate } from "hooks";
import Pagination from "components/base/pagination/Pagination";
import { RouterQuery } from "utils/constants";
import NoData from "components/base/no-data/NoData";
import BetsHistoryTools from "../tools/BetsHistoryTools";
import styles from "../sport/sport-bets-history.module.sass";
import CasinoBetView from "./bet-row/CasinoBetRow";

function getFilterQuery(filter: ICasinoBetsFilterState): IFilter<ICasinoBet>[] {
    const filterQuery: IFilter<ICasinoBet>[] = [];

    if (filter.from) {
        filterQuery.push({
            kind: FilterKind.DateFilter,
            field: "created_at",
            value: format(filter.from, "yyyy-MM-dd HH:mm:ss"),
            expression: ComparisonFilterExpression.EqualOrGreaterThan,
        });
    }

    if (filter.to) {
        filterQuery.push({
            kind: FilterKind.DateFilter,
            field: "created_at",
            value: format(filter.to, "yyyy-MM-dd HH:mm:ss"),
            expression: ComparisonFilterExpression.EqualOrLessThan,
        });
    }

    if (filter.status) {
        filterQuery.push({
            kind: FilterKind.StringFilter,
            field: "status",
            value: filter.status,
            expression: StringFilterExpression.Equal,
        });
    }

    if (filter.search) {
        filterQuery.push({
            kind: FilterKind.StringFilter,
            field: "short_id",
            value: filter.search,
            expression: StringFilterExpression.Match,
        });
    }

    if (filter.gameTypes.length) {
        filterQuery.push({
            kind: FilterKind.StringChoicesFilter,
            field: "game_type",
            value: filter.gameTypes,
            expression: ChoicesFilterExpression.In,
        });
    }

    return filterQuery;
}

type IBetsState = {
    bets: ICasinoBet[];
    loading: boolean;
};

export type ICasinoBetsFilterState = {
    status: string;
    from: Date | null;
    to: Date | null;
    search: string;
    gameTypes: CasinoGameTypeEnum[];
};

type IProps = {
    isPageMode?: boolean;
};

const CasinoBetsHistory: FC<IProps> = ({ isPageMode = false }) => {
    const searchParams = useSearchParams();
    const params = useParams();
    const client = useContext(ClientBFFContext);
    const { startOfDay, subDays, endOfDay } = useDate();
    const [betsState, setBetsState] = useState<IBetsState>({
        bets: [],
        loading: false,
    });
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const scope = useMemo(
        () => (isPageMode ? params.scope : searchParams?.get("scope")),
        [searchParams, params, isPageMode],
    );

    const gameTypesPayload = useMemo(() => {
        switch (scope) {
            case RouterQuery.LiveCasio:
                return CasinoScopesMap.livecasino;
            case RouterQuery.Slots:
                return CasinoScopesMap.slots;
            default:
                return [];
        }
    }, [scope]);

    const [filter, setFilter] = useState<ICasinoBetsFilterState>({
        from: subDays(startOfDay(new Date()), 7),
        to: endOfDay(new Date()),
        status: "",
        search: "",
        gameTypes: gameTypesPayload,
    });

    const handleFilterChange = useCallback((updatedFilter: ICasinoBetsFilterState) => {
        setFilter(updatedFilter);
        setPagination((state) => ({
            ...state,
            page: 1,
        }));
    }, []);

    const onPageChangeHandler = (page: number) => {
        setPagination((state) => ({
            ...state,
            page,
        }));
    };

    const onPerPageHandler = (val: string) => {
        setPagination((state) => ({
            ...state,
            items_per_page: parseInt(val),
            page: 1,
        }));
    };

    useDidUpdateEffect(() => {
        if (scope) {
            handleFilterChange({ ...filter, gameTypes: gameTypesPayload });
        }
    }, [scope]);

    useEffect(() => {
        let isMounted = true;
        const loadBets = async () => {
            setBetsState((state) => ({
                ...state,
                loading: true,
            }));

            try {
                const response = await client.queryCasinoBets({
                    page: pagination.page,
                    items_per_page: pagination.items_per_page,
                    order_by: [
                        {
                            field: "created_at",
                            order: Order.DESC,
                        },
                    ],
                    filter: getFilterQuery(filter),
                });

                if (isMounted) {
                    setBetsState((state) => ({
                        ...state,
                        loading: false,
                        bets: response.items,
                    }));
                    setPagination((state) => ({
                        ...state,
                        total_pages: response.total_pages,
                    }));
                }
            } catch (_error: unknown) {
                if (isMounted) {
                    setBetsState((state) => ({
                        ...state,
                        loading: false,
                    }));
                }
            }
        };

        void loadBets();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, pagination.items_per_page, pagination.page]);

    return (
        <ErrorBoundary>
            <BetsHistoryTools onChange={handleFilterChange} filter={filter} />
            <div className={styles.content} data-testid="casino-bet-history-content">
                {betsState.loading && <Loading testId="loading-spinner" />}
                {!betsState.loading && (
                    <>
                        {betsState.bets?.map((bet) => (
                            <CasinoBetView key={bet.id} bet={bet} />
                        ))}
                        {betsState.bets && betsState.bets.length === 0 && <NoData />}
                    </>
                )}
            </div>
            {betsState.bets && !!betsState.bets.length && (
                <Pagination
                    items_per_page={pagination.items_per_page}
                    page={pagination.page}
                    total_pages={pagination.total_pages || 0}
                    onPageChange={onPageChangeHandler}
                    onPerPage={onPerPageHandler}
                />
            )}
        </ErrorBoundary>
    );
};

export default CasinoBetsHistory;
